import { ANC_HUB_OTHERS_CATEGORY, PRESTATAIRE_TYPE_CATEGORIES, PrestataireTypeValue } from './constants';

export interface PrestataireUser {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
}

export interface HomologationMedia {
    '@id'?: string;
    id: string;
    name?: string;
    contentUrlTokenAccess?: string;
}

export interface PrestataireCompanySummary {
    '@id'?: string;
    id?: string;
    name?: string;
    email?: string | null;
    invoiceId?: string;
    street?: string | null;
    postalCode?: string | null;
    city?: string | null;
}

export interface PrestataireScope {
    type: PrestataireTypeValue | string;
    departmentCodes: number[];
}

export interface PrestataireAffiliation {
    '@id'?: string;
    id: string;
    scopes?: PrestataireScope[];
    types: PrestataireTypeValue[] | string[];
    departmentCodes: number[];
    homologationMedia?: HomologationMedia | string | null;
    prestataireUsers?: PrestataireUser[];
    fabricant?: PrestataireCompanySummary;
    prestataire?: PrestataireCompanySummary | string;
}

export interface PrestataireListRow {
    key: string;
    affiliationId: string;
    companyName: string;
    companyEmail: string;
    invoiceId: string;
    street?: string;
    postalCode?: string;
    city?: string;
    types: string[];
    departmentCodes: number[];
    scopes: PrestataireScope[];
    homologationMedia: HomologationMedia | null;
}

/** Identify the existing company by SIRET (preferred) or email. The homologation PDF can be attached later. */
export interface LinkPrestatairePayload {
    siret?: string;
    email?: string;
    scopes: PrestataireScope[];
    homologationMediaId?: string;
}

export interface PrestataireSiretLookup {
    siret: string;
    found: boolean;
    /** Only OTHERS installer / maintainer / emptying companies can be affiliated. */
    eligible?: boolean;
    alreadyAffiliated?: boolean;
    company: PrestataireCompanySummary | null;
}

/** Generic coreapi-v2 company creation (POST /anc/companies), same contract as the ANC mobile app. */
export interface CreateCompanyPayload {
    name: string;
    invoiceId: string;
    adminUsername: string;
    adminEmail: string;
    adminPassword: string;
    adminName?: string;
    adminLastName?: string;
    street?: string;
    additionalAddress?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    /** Company categories (OTHERS + sub-types), resolved from the scope types by categoryTypesFromScopes. */
    categoryTypes: number[];
    /** Optional: also links the new company as provider of the current fabricant, in the same request. */
    provider?: {
        scopes: PrestataireScope[];
        homologationMediaId?: string;
    };
}

export interface UpdatePrestatairePayload {
    scopes: PrestataireScope[];
    homologationMediaId?: string;
}

export const scopesFromAffiliation = (affiliation: {
    scopes?: PrestataireScope[];
    types?: string[];
    departmentCodes?: number[];
}): PrestataireScope[] => {
    if (Array.isArray(affiliation.scopes) && affiliation.scopes.length > 0) {
        return affiliation.scopes.map((scope) => ({
            type: scope.type,
            departmentCodes: [...(scope.departmentCodes || [])],
        }));
    }
    const types = affiliation.types || [];
    const departmentCodes = affiliation.departmentCodes || [];
    return types.map((type) => ({ type, departmentCodes: [...departmentCodes] }));
};

/** OTHERS + the sub-type categories of every checked "Type de prestation", deduplicated. */
export const categoryTypesFromScopes = (scopes: PrestataireScope[]): number[] =>
    Array.from(
        new Set([
            ANC_HUB_OTHERS_CATEGORY,
            ...scopes.flatMap((scope) => PRESTATAIRE_TYPE_CATEGORIES[scope.type as PrestataireTypeValue] || []),
        ]),
    );

export const scopesAreValid = (scopes: PrestataireScope[]): boolean =>
    scopes.length > 0 && scopes.every((scope) => (scope.departmentCodes || []).length > 0);
